import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Field, Form } from 'react-final-form';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import {
    useTranslate,
    useNotify,
    Notification,
    Link
} from 'react-admin';
import { ThemeProvider } from '@material-ui/styles';
import { createTheme, makeStyles } from '@material-ui/core/styles';
import SecurityIcon from '@material-ui/icons/Security';
import TextField from '@material-ui/core/TextField';
import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import {
    Typography,
    Card,
    CardActions,
    CardContent
} from '@material-ui/core';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import DeleteIcon from '@material-ui/icons/Delete';
import ImageList from '@material-ui/core/ImageList';
import ImageListItem from '@material-ui/core/ImageListItem';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import { lightTheme } from '../layout/themes';
import URL from '../URL';

const useStyles = makeStyles(theme => ({
    comment: {
        minWidth: 275,
    },
    main: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        alignItems: 'center',
        justifyContent: 'flex-start',
        background: 'url(/img/fondo1.jpg)',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
    },
    card: {
        minWidth: 200,
        maxWidth: 800,
        marginTop: '3em',
    },
    avatar: {
        margin: '1em',
        display: 'flex',
        justifyContent: 'center',
    },
    icon: {
        backgroundColor: theme.palette.secondary.main,
    },
    hint: {
        marginTop: '1em',
        marginBottom: '1em',
        display: 'flex',
        justifyContent: 'center',
        color: theme.palette.grey[500],
        padding: '10px'
    },
    id: {
        marginTop: '1em',
        display: 'flex',
        justifyContent: 'center',
        color: theme.palette.grey[800],
        padding: '10px',
        fontStyle: 'italic'
    },
    status: {
        display: 'flex',
        justifyContent: 'center',
        color: theme.palette.grey[800],
        padding: '10px'
    },
    description: {
        marginTop: '1em',
        display: 'flex',
        justifyContent: 'left',
        color: theme.palette.grey[1000],
        padding: '10px'
    },
    form: {
        padding: '0 1em 1em 1em',
    },
    input: {
        marginTop: '1em',
    },
    select: {
        marginTop: '1em',
        width: '100%'
    },
    actions: {
        padding: '0 1em 1em 1em',
    },
    link: {
        textDecoration: 'none',
        color: 'inherit',
    },
    bold: {
        fontWeight: 'bold',
        paddingLeft: '10px'
    },
    italic: {
        fontWeight: 'italic'
    },
    table: {
        marginLeft: '1em',
        marginRight: '1em',
        border: "1px solid #ddd",
        width: '94%'
    },
    return: {
        marginTop: '1em',
        display: 'flex',
        justifyContent: 'right',
        color: theme.palette.grey[500],
        padding: '10px'
    },
    documents: {
        width: '100%',
        padding: theme.spacing(0, 2, 2),
    },
    documentLink: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        textDecoration: 'none',
        color: 'inherit',
        borderRadius: theme.shape.borderRadius,
        overflow: 'hidden',
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        '&:hover': {
            boxShadow: theme.shadows[2],
        },
    },
    documentImage: {
        display: 'block',
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    documentFile: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(1),
        textAlign: 'center',
        height: '100%',
    },
    documentPdf: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
    },
    pdfPreview: {
        flex: 1,
        width: '100%',
        border: 0,
        pointerEvents: 'none',
        backgroundColor: theme.palette.grey[200],
    },
    pdfLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        padding: theme.spacing(0.5, 1),
        width: '100%',
        backgroundColor: theme.palette.grey[100],
    },
    documentName: {
        marginTop: theme.spacing(0.5),
        wordBreak: 'break-word',
    },
    fileInput: {
        display: 'none',
    },
    fileList: {
        marginTop: '0.5em',
    },
    fileItem: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
}));

const getFileExtension = (file) => {
    const ext = file?.ext || '';
    if (ext) {
        return ext.toLowerCase().replace(/^\./, '');
    }
    const match = (file?.name || '').match(/\.([^.]+)$/);
    return match ? match[1].toLowerCase() : '';
};

const getFileMime = (file) => (file?.mime || file?.mimetype || '').toLowerCase();

const isPdfFile = (file) => {
    const mime = getFileMime(file);
    if (mime === 'application/pdf') {
        return true;
    }
    return getFileExtension(file) === 'pdf';
};

const isImageFile = (file) => {
    if (isPdfFile(file)) {
        return false;
    }
    const mime = getFileMime(file);
    if (mime.startsWith('image/')) {
        return true;
    }
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(getFileExtension(file));
};

const renderDocumentPreview = (file, classes) => {
    if (isImageFile(file)) {
        return (
            <img
                src={file.url}
                alt={file.name}
                className={classes.documentImage}
            />
        );
    }

    if (isPdfFile(file)) {
        return (
            <div className={classes.documentPdf}>
                <div className={classes.pdfLabel}>
                    <PictureAsPdfIcon fontSize="small" color="error" />
                    <Typography variant="caption" noWrap className={classes.documentName}>
                        {file.name}
                    </Typography>
                </div>
            </div>
        );
    }

    return (
        <div className={classes.documentFile}>
            <InsertDriveFileIcon fontSize="large" color="action" />
            <Typography
                variant="caption"
                component="span"
                className={classes.documentName}
            >
                {file.name}
            </Typography>
        </div>
    );
};

const renderInput = ({
    meta: { touched, error } = {},
    input: { ...inputProps },
    ...props
}) => (
    <TextField
        error={!!(touched && error)}
        helperText={touched && error}
        {...inputProps}
        {...props}
        fullWidth
    />
);

const ComplaintView = ({permissions, ...props}) => {
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [data, setData] = useState();
    const [selectedFiles, setSelectedFiles] = useState([]);
    const fileInputRef = useRef(null);
    const translate = useTranslate();
    const classes = useStyles();
    const notify = useNotify();
    let location = useLocation();

    const id = location?.pathname.split('/')[2] || ''

    const fetchComplaint = useCallback(() => {
        setLoading(true);
        return axios.get(`${URL}/complaints/${id}`)
            .then(({ data: complaint }) => {
                setData(complaint);
                return complaint;
            })
            .catch(() => {
                notify('resources.complaints.public.findError', 'warning');
            })
            .finally(() => {
                setLoading(false);
            });
    }, [id, notify]);

    useEffect(() => {
        fetchComplaint();
    }, [fetchComplaint]);

    const handleFileChange = (event) => {
        const files = Array.from(event.target.files || []);
        if (files.length) {
            setSelectedFiles(prev => [...prev, ...files]);
        }
        event.target.value = '';
    };

    const handleRemovePendingFile = (index) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleUploadFiles = () => {
        if (!selectedFiles.length) {
            return;
        }

        const complaintId = data?.id || id;
        const formData = new FormData();
        selectedFiles.forEach(file => {
            formData.append('files', file);
        });
        formData.append('ref', 'complaint');
        formData.append('refId', complaintId);
        formData.append('field', 'images');

        setUploading(true);
        notify('pos.uploading', 'info', {}, false);

        axios.post(`${URL}/upload`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        })
            .then(() => {
                setSelectedFiles([]);
                notify('pos.changed', 'info', {}, false);
                return fetchComplaint();
            })
            .catch(() => {
                notify('pos.upload_failed', 'warning');
            })
            .finally(() => {
                setUploading(false);
            });
    };

    const comment = ({comment}) => {
        setLoading(true);
        // Request API.
        axios.post(`${URL}/complaint-comments`, {
              complaintId: data.id,
              comment
          })
          .then(() => {
              notify(
                  `pos.comment_sent`,
                  'info',
                  {},
                  false
              );
              setTimeout(() => window.location.reload(), 2000)
          })
          .catch(error => {
              setLoading(false);
              notify(
                  typeof error === 'string'
                      ? error
                      : typeof error === 'undefined' || !error.message
                      ? 'resources.issues.notification.comment_error'
                      : error.message,
                  'warning'
              );
          });
    };

    function createMarkup(content) {
        return {__html: content };
    }

    return (
        <Form
        onSubmit={comment}
        render={({ handleSubmit: comment }) => (
        <form onSubmit={comment} noValidate>
            <div className={classes.main}>
                <Card className={classes.card}>
                    <Link
                        to="/complaint-find"
                        className={classes.link}
                        key={id}
                    >
                        <div className={classes.avatar}>
                            <Avatar className={classes.icon}>
                                <SecurityIcon />
                            </Avatar>
                        </div>
                    </Link>
                    <div className={classes.hint}>
                        {translate('resources.complaints.public.title')}
                    </div>

                    <div className={classes.id}>
                        ~{translate('resources.complaints.public.data')}~
                    </div>

                    <div className={classes.tableContainer}>
                        <table className={classes.table}>
                            <tr>
                                <td>ID</td>
                                <td className={classes.bold}>{id}</td>
                            </tr>
                            <tr>
                                <td>{translate('resources.complaints.fields.status')}</td>
                                <td className={classes.bold}>
                                    {translate(`resources.complaints.status.${data?.status}`)}
                                    {data?.status === 'new' ? ' 🆕' : data?.status === 'in_progress' ? ' ⚙️' : ' ✅'}
                                </td>
                            </tr>
                            <tr>
                                <td>{translate('resources.complaints.fields.createdAt')}</td>
                                <td className={classes.bold}>{new Date(data?.createdAt).toLocaleDateString()}</td>
                            </tr>
                            <tr>
                                <td>{translate('resources.complaints.fields.was')}</td>
                                <td className={classes.bold}>{new Date(data?.date).toLocaleDateString()}</td>
                            </tr>
                        </table> 
                    </div>
                    <div className={classes.id}>
                        ~{translate('resources.complaints.fields.description')}~
                    </div>
                    <div className={classes.description}>
                        {data?.description}
                    </div>
                    {
                        data?.involved ? <>
                            <div className={classes.id}>
                                ~{translate('resources.complaints.fields.involved')}~
                            </div>
                            <div className={classes.description}>
                                {data?.involved}
                            </div>
                        </> : <></>
                    }
                    <div className={classes.id}>
                        ~{translate('resources.complaints.fields.documents')}~
                    </div>
                    {data?.images?.length > 0 && (
                        <ImageList
                            cols={3}
                            rowHeight={160}
                            className={classes.documents}
                        >
                            {data.images.map(file => (
                                <ImageListItem key={file.id}>
                                    <a
                                        href={file.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={classes.documentLink}
                                        title={file.name}
                                    >
                                        {renderDocumentPreview(file, classes)}
                                    </a>
                                </ImageListItem>
                            ))}
                        </ImageList>
                    )}
                    {
                        data?.status !== 'resolved' &&
                        <div className={classes.form}>
                            <input
                                ref={fileInputRef}
                                className={classes.fileInput}
                                type="file"
                                multiple
                                accept="application/pdf, image/*"
                                onChange={handleFileChange}
                            />
                            <Button
                                variant="outlined"
                                startIcon={<AttachFileIcon />}
                                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                                disabled={loading || uploading}
                                fullWidth
                            >
                                {translate('resources.complaints.fields.attachments')}
                            </Button>
                            {selectedFiles.length > 0 && (
                                <div className={classes.fileList}>
                                    {selectedFiles.map((file, index) => (
                                        <div key={`${file.name}-${index}`} className={classes.fileItem}>
                                            <Typography variant="body2" noWrap>
                                                {file.name}
                                            </Typography>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleRemovePendingFile(index)}
                                                disabled={loading || uploading}
                                                aria-label="remove pending file"
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </div>
                                    ))}
                                    <Typography variant="caption" color="textSecondary">
                                        {translate('pos.upload_remove_help')}
                                    </Typography>
                                </div>
                            )}
                            <div className={classes.input}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    type="button"
                                    onClick={handleUploadFiles}
                                    disabled={loading || uploading || !selectedFiles.length}
                                    fullWidth
                                >
                                    {translate('pos.submit')}
                                </Button>
                            </div>
                        </div>
                    }
                    <div className={classes.id}>
                        ~{translate('resources.complaints.fields.comments')}~
                    </div>
                    {
                        data?.comments?.sort((a, b) => a.createdAt < b.createdAt).map(comment => (
                            <Card className={classes.comment} key={comment.id}>
                                <CardContent>
                                    <Typography variant="h6" component="h2">
                                        { comment.author ? translate('resources.complaints.public.comite') : translate('resources.complaints.public.user') }
                                    </Typography>
                                    <Typography className={classes.pos} color="textSecondary">
                                        { (new Date(comment.createdAt)).toLocaleString() }
                                    </Typography>
                                    <Typography variant="body2" component="p">
                                        <div dangerouslySetInnerHTML={createMarkup(comment.comment)}></div>
                                    </Typography>
                                </CardContent>
                            </Card>
                        ))
                    }
                    {
                        data?.status !== 'resolved' &&
                        <>
                            <div className={classes.form}>
                                <div className={classes.input}>
                                    <Field
                                        name="comment"
                                        component={renderInput}
                                        label={translate('resources.complaints.fields.comment')}
                                        disabled={loading}
                                        multiline
                                        minRows={6}
                                        maxRows={6}
                                    />
                                </div>
                            </div>
                            <CardActions className={classes.actions}>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    type="submit"
                                    disabled={loading}
                                    className={classes.button}
                                >
                                    {translate('resources.issues.action.comment')}
                                </Button>
                            </CardActions>
                        </>
                    }
                    <div className={classes.return}>
                        <Link
                            to="/complaint-find"
                            className={classes.link}
                            key={id}
                        >
                            {translate('pos.return')}
                        </Link>
                    </div>
                </Card>
                <Notification />
            </div>
        </form>
      )}
    />)
};

ComplaintView.propTypes = {
    authProvider: PropTypes.func,
    previousRoute: PropTypes.string,
};

// We need to put the ThemeProvider decoration in another component
// Because otherwise the useStyles() hook used in ResetPassword won't get
// the right theme
const ComplaintViewWithTheme = props => (
    <ThemeProvider theme={createTheme(lightTheme)}>
        <ComplaintView {...props} />
    </ThemeProvider>
);

export default ComplaintViewWithTheme;
